import { useQuery } from "@tanstack/react-query";
import { tournamentsQueryOptions } from "@/api/tournaments";
import { useState, useEffect, createContext, useContext } from "react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import type { Tournament } from "@/api/tournaments";
import { ScoutingHeader } from "@/components/general/Scouting/ScoutingHeader";
import ScoutingNavigation from "@/components/general/Scouting/ScoutingNavigation";
import type { ScoutingStatCategory } from "@/components/general/Scouting/ScoutingNavigation";
import { useSelectedTournament } from "@/context/tournamentContext";
import { useSelectedTeam } from "@/context/teamContext";
import { exportScoutingPlayersToExcel, exportScoutingGoalkeepersToExcel } from "@/api/excelExport";
import { RouteUrl } from "@/configs/router/routes";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { ANALYST_TIER_ROLES } from "@/context/rolesContext";

interface ScoutingContextValue {
    selectedTournament: string;
    setSelectedTournament: (tournament: string) => void;
    selectedTournamentId: number | undefined;
    tournaments: Tournament[] | undefined;
    statCategory: ScoutingStatCategory;
    setStatCategory: (category: ScoutingStatCategory) => void;
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    positionFilter: string[];
    setPositionFilter: (p: string[]) => void;
    teamFilter: string[];
    setTeamFilter: (t: string[]) => void;
    availablePositions: string[];
    setAvailablePositions: (positions: string[]) => void;
    availableTeams: string[];
    setAvailableTeams: (teams: string[]) => void;
}

const ScoutingContext = createContext<ScoutingContextValue | undefined>(undefined);

export const useScoutingContext = () => {
    const context = useContext(ScoutingContext);
    if (!context) {
        throw new Error("useScoutingContext must be used within a ScoutingProvider");
    }
    return context;
};

export default function ScoutingAnalysisPage() {
    return (
        <ProtectedRoute requiredRole={ANALYST_TIER_ROLES}>
            <ScoutingProvider>
                <ScoutingContent />
            </ScoutingProvider>
        </ProtectedRoute>
    );
}

function ScoutingProvider({ children }: { children: React.ReactNode }) {
    const { tournamentId: globalTournamentId, setTournamentId: setGlobalTournamentId } = useSelectedTournament();
    const { teamId } = useSelectedTeam();
    const { data: tournaments } = useQuery(tournamentsQueryOptions(teamId));
    const [selectedTournament, setSelectedTournament] = useState("");
    const [statCategory, setStatCategory] = useState<ScoutingStatCategory>("overview");
    const [searchQuery, setSearchQuery] = useState("");
    const [positionFilter, setPositionFilter] = useState<string[]>([]);
    const [teamFilter, setTeamFilter] = useState<string[]>([]);
    const [availablePositions, setAvailablePositions] = useState<string[]>([]);
    const [availableTeams, setAvailableTeams] = useState<string[]>([]);

    const mostRecentTournament = tournaments
        ?.filter((t) => t.matches && t.matches.length > 0)
        ?.reduce(
            (latest, current) => (latest == undefined || current.id > latest.id ? current : latest),
            undefined as unknown as Tournament
        );

    useEffect(() => {
        if (globalTournamentId && tournaments) {
            const globalTournament = tournaments.find((t) => t.id === globalTournamentId);
            if (globalTournament && selectedTournament === "") {
                setSelectedTournament(globalTournament.name);
            }
        } else if (mostRecentTournament && selectedTournament === "") {
            setSelectedTournament(mostRecentTournament.name);
        }
    }, [mostRecentTournament, selectedTournament, globalTournamentId, tournaments]);

    const selectedTournamentId = tournaments?.find((t) => t.name === selectedTournament)?.id;

    useEffect(() => {
        setSearchQuery("");
        setPositionFilter([]);
        setTeamFilter([]);
    }, [selectedTournamentId]);

    useEffect(() => {
        if (selectedTournamentId) {
            setGlobalTournamentId(selectedTournamentId);
        }
    }, [selectedTournamentId, setGlobalTournamentId]);

    const value: ScoutingContextValue = {
        selectedTournament,
        setSelectedTournament,
        selectedTournamentId,
        tournaments,
        statCategory,
        setStatCategory,
        searchQuery,
        setSearchQuery,
        positionFilter,
        setPositionFilter,
        teamFilter,
        setTeamFilter,
        availablePositions,
        setAvailablePositions,
        availableTeams,
        setAvailableTeams,
    };

    return <ScoutingContext.Provider value={value}>{children}</ScoutingContext.Provider>;
}

function ScoutingContent() {
    const {
        tournaments,
        selectedTournament,
        setSelectedTournament,
        selectedTournamentId,
        statCategory,
        setStatCategory,
        searchQuery,
        setSearchQuery,
        positionFilter,
        setPositionFilter,
        teamFilter,
        setTeamFilter,
        availablePositions,
        availableTeams,
    } = useScoutingContext();

    const { teamId } = useSelectedTeam();
    const pathname = useRouterState({ select: (state) => state.location.pathname });
    const isPlayersPage = pathname === RouteUrl.ScoutingPlayers;

    const filters = {
        search: searchQuery || undefined,
        teams: teamFilter.length > 0 ? teamFilter : undefined,
        positions: positionFilter.length > 0 ? positionFilter : undefined,
    };

    const handleExportToExcel = selectedTournamentId
        ? () =>
              isPlayersPage
                  ? exportScoutingPlayersToExcel(selectedTournamentId, filters, teamId)
                  : exportScoutingGoalkeepersToExcel(selectedTournamentId, filters, teamId)
        : undefined;

    return (
        <main className="flex-1 flex flex-col p-4 min-h-0">
            <ScoutingHeader
                selectedTournament={selectedTournament}
                onTournamentChange={setSelectedTournament}
                tournaments={tournaments}
            />
            <ScoutingNavigation
                statCategory={statCategory}
                onStatCategoryChange={setStatCategory}
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                positionFilter={positionFilter}
                onPositionFilterChange={setPositionFilter}
                teamFilter={teamFilter}
                onTeamFilterChange={setTeamFilter}
                availablePositions={availablePositions}
                availableTeams={availableTeams}
                onExportToExcel={handleExportToExcel}
            />
            <div className="mt-2 md:flex-1 md:min-h-0 md:overflow-auto">
                <Outlet />
            </div>
            <div className="md:hidden py-10" />
        </main>
    );
}
